<template>
  <div class="pb-3">
    <base-date-picker
      v-if="field.type === 'Date'"
      :label="getLabel"
      label-cols-sm="4"
      @input="emitInput"
      v-model="inputValue"
      :asterix="field.required"
      :description="field.description"
      :show-picker="field.key !== 'BirthDate'"
      :class="'input-DatePicker-field'"
      :rules="{ required: field.required }"
    />
    <b-form-group v-else label-cols-sm="4">
      <template #label>
        {{ getLabel }}
        <span v-if="isRequired">*</span>
        <span
          v-if="field.description"
          v-b-popover.hover.top="field.description"
        >
          <b-icon-question-circle variant="info" />
        </span>
      </template>
      <validation-provider
        :ref="field.key + 'ValidationProvider'"
        :rules="fieldValidationRules"
        v-slot="{ errors }"
        :name="getLabel"
        :mode="validationMode"
        :vid="field.key"
        :skipIfEmpty="field.type == 'MarketingInterests' ? false : true"
      >
        <b-form-input
          v-if="field.type === 'String' || field.type === 'Email' || field.key === 'ConfirmEmail'"
          v-model="inputValue"
          @input="emitInput"
          @blur="formatInitialsOnBlur"
          trim
        />

        <b-input-group v-if="field.type === 'Password'">
          <b-form-input
            v-model="inputValue"
            @input="emitInput"
            trim
            :type="passwordFieldInputType"
            autocomplete="new-password"
          />
          <b-input-group-append>
            <b-button
              variant="outline-secondary"
              @click="togglePasswordVisibility"
            >
              <b-icon-eye-fill />
            </b-button>
          </b-input-group-append>
        </b-input-group>

        <b-form-input
          v-if="field.type === 'Number'"
          v-model="inputValue"
          @input="emitInput"
          trim
          type="number"
        />

        <b-form-checkbox
          v-model="inputValue"
          v-if="field.type === 'Boolean'"
          @input="emitInput"
        />

        <b-form-radio-group
          v-if="field.key === 'Gender'"
          v-model="inputValue"
          @input="emitInput"
          :options="genderOptions"
          value-field="id"
          text-field="name"
          name="gender-selection"
        />

        <b-form-radio-group
          v-if="field.key === 'GenderForMatches'"
          v-model="inputValue"
          @input="emitInput"
          :options="genderForMatchesOptions"
          value-field="id"
          text-field="name"
          name="gender-for-matches-selection"
        />

        <b-form-select
          v-if="isDropdownFieldType && !field.customFieldId"
          v-model="inputValue"
          @input="emitInput"
          :options="getOptions"
          value-field="id"
          text-field="name"
        />

        <b-form-select
          v-if="isDropdownFieldType && field.customFieldId"
          v-model="inputValue"
          @input="emitInput"
          :options="getOptions"
          value-field="code"
          text-field="name"
        />

        <multiselect
          v-if="isMultiselectFieldType && field.customFieldId"
          :multiple="true"
          v-model="multiSelectValue"
          :options="getOptions"
          :placeholder="$t('select_option')"
          label="name"
          track-by="code"
        />

        <multiselect
          v-if="isMultiselectFieldType && !field.customFieldId"
          :multiple="true"
          v-model="multiSelectValue"
          :options="getOptions"
          :placeholder="$t('select_option')"
          label="name"
          track-by="id"
        />

        <b-form-select
          v-model="countrySelection"
          v-if="field.type === 'Country'"
          :options="countriesList"
          value-field="id"
          text-field="name"
        >
          <template #first v-if="!defaultCountry">
            <b-form-select-option :value="null" disabled>{{ $t('select_option') }}</b-form-select-option>
          </template>
        </b-form-select>

        <image-input
          v-if="field.type === 'Image'"
          v-model="inputValue"
          @input="emitInput"
        />

        <b-form-select
          v-if="field.type === 'Language'"
          v-model="inputValue"
          @input="emitInput"
          :options="getOptions"
          value-field="cultureInfoName"
          text-field="displayName"
        />

        <multiselect
          v-if="field.type === 'Organisation'"
          v-model="inputValue"
          @input="emitInput"
          :options="getOptions"
          :placeholder="$t('select_option')"
          label="name"
          track-by="id"
        />

        <multiselect
          v-if="
            field.type === 'Newsletters'
          "
          v-model="multiSelectValue"
          :options="getOptions"
          :placeholder="$t('select_option')"
          label="name"
          track-by="code"
          :multiple="true"
        />
        
        <b-row v-if="
            field.type === 'MarketingInterests'
          ">
          <b-col
            v-for="marketingInterest in marketingInterests"
            :key="marketingInterest.group"
            class="col-12 "
            :class="{'col-md-6': marketingInterest.length > 1}"
          >
            <b-checkbox
              v-if="marketingInterest.length > 1"
              v-model="parentTagGroups[marketingInterest[0].group]" 
              @change="toggleAllChildrenTags($event, marketingInterest[0].group)"
              class="pb-0">
                <h6 class="mb-0" :class="{ 'font-weight-normal font-italic': !marketingInterest[0].group }"> {{ marketingInterest[0].group ? marketingInterest[0].group : $t("select_all") }} </h6>
            </b-checkbox>
            <b-form-checkbox
              v-model="checkBoxesVal[marketingInterest[0].group][indx]"
              v-for="(intrestOption, indx) in marketingInterest"
              :value="intrestOption.code"
              :key="intrestOption.code"
              @change="emitInput(checkBoxesVal[marketingInterest[0].group]) && checkIfParentTagCheckboxActive()"
              class="ml-2 pt-0"
              name="marketingInterests"
              stacked
              >{{ intrestOption.name }}
            </b-form-checkbox>
          </b-col>
        </b-row>
        <p v-for="error in errors" :key="error" class="text-danger mb-0">
          {{ error }}
        </p>
      </validation-provider>
    </b-form-group>
  </div>
</template>

<script>
import { countryHelper } from "../countryHelper.js";
import { BIconQuestionCircle, BIconEyeFill } from "bootstrap-vue";
import { genderEnum, planMixin } from "@/shared";
import _ from "lodash";
import Multiselect from "vue-multiselect";
import { extend } from "vee-validate";

export default {
  mixins: [countryHelper, genderEnum, planMixin],
  components: {
    BIconQuestionCircle,
    BIconEyeFill,
    Multiselect,
  },
  name: "FormField",
  data() {
    return {
      inputValue: null, //not used but required by vee-validate
      passwordFieldInputType: "password",
      checkBoxesVal: {},
      parentTagGroups: {},
    };
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    formConfig: {
      type: Object,
      required: true,
    },
    input: {
      type: Object,
      default: () => ({}),
    },
    value: null,
  },
  created() {
    if (this.field.type === "MarketingInterests") {            
      for (const group in this.marketingInterests) {
        this.$set(this.checkBoxesVal, group, Array());
      }
      extend("min-checked-1", {
        message:
          `${this.$t("rules.select_at_least_one")}`,
        validate: () => {              
          if (this.inputValue == "" || !this.inputValue) {
            return false;
          } 
          return true;
        }
      });
    }
  },
  methods: {
    async emitInput(val) {
      if (this.field.type === "Organisation") {
        val = val.id;
      } else if (this.field.type === "MarketingInterests") {       
        val = Object.values(this.checkBoxesVal).flat().filter((el) => !!el);
        this.inputValue = val;
      }

      this.$emit("input", {
        key: this.field.key,
        value: val,
        customFieldId: this.field.customFieldId,
      });
    },
    validationMode({ errors }) {
      if (this.field.key === "VoucherCode") {
        return {
          on: ["blur", "input", "change"],
          debounce: 1000,
        };
      }
      if (errors.length) {
        return {
          on: ["input", "change"],
        };
      }

      return {
        on: ["change", "blur"],
      };
    },
    formatInitialsOnBlur() {
      if (this.field.key !== "Initials" || !this.inputValue) {
        return;
      }

      const letters = this.inputValue.match(/[A-Za-z]/g);
      if (!letters) {
        return;
      }

      const formatted = letters.map((letter) => `${letter.toUpperCase()}.`).join("");
      if (formatted !== this.inputValue) {
        this.inputValue = formatted;
        this.emitInput(formatted);
      }
    },
    togglePasswordVisibility() {
      if (this.passwordFieldInputType === "password") {
        this.passwordFieldInputType = "text";
      } else {
        this.passwordFieldInputType = "password";
      }
    },
    toggleAllChildrenTags(val, tagGroupName) {	
			if (!val) {
				if (this.checkBoxesVal[tagGroupName]) {                   
          this.$set(this.checkBoxesVal, tagGroupName, []);
				}
			} else {
				let tagCodes = this.marketingInterests[tagGroupName].map((tag) => tag.code);
				this.$set(this.checkBoxesVal, tagGroupName, tagCodes);
			}

      this.emitInput(this.checkBoxesVal[tagGroupName]);
		},
		checkIfParentTagCheckboxActive() {	
      Object.keys(this.marketingInterests).forEach(key => {
        if (!this.checkBoxesVal[key] || this.checkBoxesVal[key] == 0) return;
        const tempCheckboxesVal = this.checkBoxesVal[key].filter((el) => !!el);                
        this.$set(this.parentTagGroups, key, tempCheckboxesVal.length == this.marketingInterests[key].length);
      });
		},
  },
  computed: {
    countrySelection: {
      get: function () {
        const existingValue = _.get(this.input, this.field.key);

        if (!existingValue && this.defaultCountry) {
          this.emitInput(this.defaultCountry.id);
        }

        return existingValue || (this.defaultCountry ? this.defaultCountry.id : null);
      },
      set: function (newValue) {
        this.emitInput(newValue);
      },
    },
    multiSelectValue: {
      get: function () {
        return this.inputValue;
      },
      set: function (newValue) {
        this.inputValue = newValue;

        if (!Array.isArray(newValue)) {
          this.emitInput(null);
        }

        var parsedValue = this.field.customFieldId
          ? newValue.map((el) => el.code).join(", ")
          : newValue.map((el) => (el.id ? el.id : el.code));

        this.emitInput(parsedValue);
      },
    },
    marketingInterests() {
      let formattedMarketingIntrests = {};
      try {
        const optionTags = this.formConfig.fieldsConfiguration.filter((item) =>
          item.name == "MarketingInfo"
        )[0].fields.filter((item) => item.key == "MarketingInterests")[0].options;       
        optionTags.forEach((tag) => {
          if (!formattedMarketingIntrests[tag.group]) {
            formattedMarketingIntrests[tag.group] = [];
          }
          formattedMarketingIntrests[tag.group].push(tag);
        });
      } catch (error) {
        return [];
      }      
      return formattedMarketingIntrests
    },
    defaultCountry() {
      let localeCountryCode = null;

      if (this.formConfig && this.formConfig.locale) {
        const localeParts = this.formConfig.locale.split("-");
        if (localeParts.length > 1) {
          localeCountryCode = localeParts[1].toUpperCase();
        }
      }

      let matchedCountry = null;
      if (localeCountryCode) {
        matchedCountry = this.countries.find((el) => el.alpha2 === localeCountryCode);
      }

      if (!matchedCountry && this.formConfig.defaultCountryCode) {
        matchedCountry = this.countries.find(
          (el) => el.alpha2 === this.formConfig.defaultCountryCode
        );
      }

      return matchedCountry;
    },
    countriesList() {
      if (!this.defaultCountry) {
        return this.countries;
      }

      var countriesList = this.countries.filter(
        (el) => el.alpha2 !== this.defaultCountry.alpha2
      );

      countriesList.unshift(this.defaultCountry);

      return countriesList;
    },
    genderOptions() {
      return this.genderTypeList;
    },
    // A dedicated list rather than genderOptions: this field only has Male/Female on the
    // backend (no Unspecified enum member), so "not specified" is sent as an explicit
    // null instead of reusing Gender's Unspecified value - that clears the field honestly
    // and lets a required GenderForMatches field fail validation instead of silently
    // passing with an out-of-range value.
    // The null option is offered only when the field is optional: inputValue starts as
    // null, so on a required field it would render pre-selected (BootstrapVue checks the
    // option whose value equals the model value) while being the one choice that can
    // never pass validation.
    genderForMatchesOptions() {
      const options = this.genderTypeList.filter((el) => el.id !== this.gender.Unspecified);
      return this.field.required
        ? options
        : [...options, { id: null, name: this.$t("gender.not_specified") }];
    },
    getOptions() {
      if (this.field.type === "Organisation") {
        return this.field.options.map((el) => {
          return {
            id: el.id,
            name: el.code ? `${el.name} (${el.code})` : el.name,
          };
        });
      }
      if (this.field.key === "PlanAssignment.organisationPlanId" && this.field.options) {
        return this.field.options.map((el) => ({
          ...el,
          name: this.getOrganisationPlanLabel(el, this.formConfig),
        }));
      }
      return this.field.options;
    },
    getLabel() {
      return this.field.customLabel ? this.field.customLabel : this.field.label;
    },
    isDropdownFieldType() {
      return ["Plan", "ContributionTerms", "Dropdown"].includes(
        this.field.type
      ) && !["Gender", "GenderForMatches"].includes(this.field.key);
    },
    isRequired() {
      return this.field.required && !this.isZipcodeOptional;
    },
    isZipcodeOptional() {
      if (this.field.key !== "AddressInfo.zipcode") {
        return false;
      }

      const selectedCountryId = _.get(this.input, "AddressInfo.countryId");
      const countryId = selectedCountryId || (this.defaultCountry && this.defaultCountry.id);

      return (this.formConfig.zipcodeOptionalCountryIds || []).includes(countryId);
    },
    isMultiselectFieldType() {
      return ["Discipline", "MultiSelect"].includes(this.field.type);
    },
    fieldValidationRules() {
      let str = this.isRequired ? "required|" : "";

      if (this.field.key === "Password") {
        str += "min:8|upcase|lowcase|requireDigit|requireNonAlphaNum";
      } else if (this.field.key === "ConfirmPassword") {
        str += "confirmPassword";
      } else if (this.field.key === "ConfirmEmail") {
        str += "email|confirmEmail";
      } else if (this.field.key === "BillingInfoDto.iban") {
        str += "ibanLength|iban";
      } else if (this.field.type === "Email") {
        str += "email";
      } else if (this.field.key === "VoucherCode") {
        str += "voucher";
      } else if (this.field.key === "AddressInfo.houseNumberExtension") {
        str += "max:50";
      } else if (this.field.key === "AddressInfo.houseNumber") {
        str += "max:50";
      } else if (this.field.key === "Initials" && this.field.required) {
        str += "initials";
      } else if (this.field.key === "MarketingInterests") {
        str = this.field.required ? "min-checked-1" : "";
      }

      return str;
    },
  },
  watch: {
    value(val) {
      this.inputValue = val;
    },
  },
};
</script>